import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useActivitiesStore, SavedActivity } from '../store/activitiesStore';
import apiClient, { exportAPI } from '../services/api';
import { TrashIcon } from '@heroicons/react/24/outline';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';
import { marked } from 'marked';

// Helper function to generate UUIDs
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

interface LetterField {
  label: string;
  value: string;
  placeholder?: string;
  isMultiline?: boolean;
}

interface LetterType {
  id: string;
  name: string;
}

export const LegalLetters: React.FC = () => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [letterType, setLetterType] = useState('');
  const [loading, setLoading] = useState(false);
  const [letterFields, setLetterFields] = useState<LetterField[]>([]);
  const [showLetterFields, setShowLetterFields] = useState(false);
  const [letterTemplate, setLetterTemplate] = useState('');
  const [generatedLetter, setGeneratedLetter] = useState('');
  const [showGeneratedLetter, setShowGeneratedLetter] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Template data from API
  const [availableCountries, setAvailableCountries] = useState<string[]>([]);
  const [availableStates, setAvailableStates] = useState<string[]>([]);
  const [availableLetterTypes, setAvailableLetterTypes] = useState<LetterType[]>([]);
  const [noTemplatesAvailable, setNoTemplatesAvailable] = useState(false);

  // Activity management from store
  const { addActivity, updateActivity, currentActivityId, setCurrentActivityId, getActivityById } = useActivitiesStore();

  // Fetch available countries on component mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await apiClient.get('/legal-letters/countries');
        setAvailableCountries(response.data);
      } catch (error) {
        console.error('Error fetching countries:', error);
      }
    };
    fetchCountries();
  }, []);

  // Fetch available states when country changes
  useEffect(() => {
    const fetchStates = async () => {
      if (!country) {
        setAvailableStates([]);
        return;
      }

      try {
        const response = await apiClient.get(`/legal-letters/countries/${encodeURIComponent(country)}/states`);
        setAvailableStates(response.data);
      } catch (error) {
        console.error('Error fetching states:', error);
        setAvailableStates([]);
      }
    };
    fetchStates();
  }, [country]);

  // Fetch available letter types when state changes
  useEffect(() => {
    const fetchLetterTypes = async () => {
      if (!country || !state) {
        setAvailableLetterTypes([]);
        setNoTemplatesAvailable(false);
        return;
      }

      try {
        const response = await apiClient.get(`/legal-letters/countries/${encodeURIComponent(country)}/states/${encodeURIComponent(state)}/letter-types`);
        setAvailableLetterTypes(response.data);
        setNoTemplatesAvailable(response.data.length === 0);
      } catch (error: any) {
        console.error('Error fetching letter types:', error);
        setAvailableLetterTypes([]);
        // Check if it's a 404 (no templates found)
        if (error.response?.status === 404) {
          setNoTemplatesAvailable(true);
        } else {
          setNoTemplatesAvailable(false);
        }
      }
    };
    fetchLetterTypes();
  }, [country, state]);

  // Clear current activity and reset form when navigating without a specific activity to load
  useEffect(() => {
    const locationState = location.state as { loadActivityId?: string; clearActivity?: boolean } | null;
    if (!locationState?.loadActivityId || locationState?.clearActivity) {
      setCurrentActivityId(null);
      setCountry('');
      setState('');
      setLetterType('');
      setLetterFields([]);
      setShowLetterFields(false);
      setGeneratedLetter('');
      setShowGeneratedLetter(false);
      setLetterTemplate('');
    }
  }, [location.pathname, location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear state when country changes
  useEffect(() => {
    setState('');
    setNoTemplatesAvailable(false);
  }, [country]);

  // Clear letter type when state changes
  useEffect(() => {
    setLetterType('');
  }, [state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!country.trim()) {
      alert(t('legalLetters:selectCountryFirst'));
      return;
    }

    if (availableStates.length > 0 && !state.trim()) {
      alert(t('legalLetters:selectStateFirst'));
      return;
    }

    if (!letterType.trim()) {
      alert(t('legalLetters:selectLetterTypeFirst'));
      return;
    }

    setLoading(true);

    try {
      const response = await apiClient.get(
        `/legal-letters/template/${encodeURIComponent(country)}/${encodeURIComponent(state)}/${encodeURIComponent(letterType)}`,
        {
          headers: {
            'Accept-Language': i18n.language
          }
        }
      );

      const template = response.data.content;
      setLetterTemplate(template);

      // Parse the template to extract field placeholders
      const fieldPatterns = [
        /\[([^\]]+)\]/g,           // [Field Name]
      ];

      const fieldSet = new Set<string>();

      for (const pattern of fieldPatterns) {
        let match;
        while ((match = pattern.exec(template)) !== null) {
          const fieldName = match[1].trim();
          // Skip checkbox markers, signature lines, and very short fields
          if (fieldName.length > 2 && !fieldName.startsWith('☐') && !/signature/i.test(fieldName)) {
            fieldSet.add(fieldName);
          }
        }
      }

      const fieldLabels = Array.from(fieldSet);
      const fields: LetterField[] = fieldLabels.map(fieldName => {
        // Check if this field should be multiline (description fields)
        const isMultiline = /\b(description|detailed|explain|narrative|summary)\b/i.test(fieldName);

        return {
          label: fieldName,
          value: '',
          isMultiline
        };
      });

      setLetterFields(fields);
      setShowLetterFields(true);
    } catch (error) {
      console.error('Error getting letter template:', error);
      alert(t('legalLetters:errorLoadingTemplate'));
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (index: number, value: string) => {
    setLetterFields(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], value };
      return updated;
    });
  };

  const handleGenerate = async () => {
    setLoading(true);

    try {
      let filledTemplate = letterTemplate;

      // Fill in the template with user-provided field values
      letterFields.forEach(field => {
        if (field.value.trim()) {
          const escapedLabel = field.label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const fieldPattern = new RegExp(`\\[${escapedLabel}\\]`, 'gi');
          filledTemplate = filledTemplate.replace(fieldPattern, field.value);
        }
      });

      setGeneratedLetter(filledTemplate);
      setShowGeneratedLetter(true);

      // Auto-save to activities
      const letterTypeName = availableLetterTypes.find(lt => lt.id === letterType)?.name || letterType;
      const recipientField = letterFields.find(f =>
        f.label.toLowerCase().includes('recipient') ||
        f.label.toLowerCase().includes('defendant')
      );
      const recipientName = recipientField?.value || 'Unknown';

      const savedActivity = {
        id: currentActivityId || generateUUID(),
        name: `${letterTypeName}: ${recipientName}`,
        uploadedFiles: [],
        selectedFiles: new Set<number>(),
        actionHistory: [],
        savedAt: new Date(),
        activityType: 'legal-letters' as const,
      };

      if (currentActivityId) {
        updateActivity(savedActivity);
      } else {
        addActivity(savedActivity);
        setCurrentActivityId(savedActivity.id);
      }
    } catch (error) {
      console.error('Error generating letter:', error);
      alert('Failed to generate letter. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format: 'word' | 'pdf' | 'text') => {
    await exportDocument(format);
  };

  const printLetter = () => {
    if (!generatedLetter) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const letterTypeName = availableLetterTypes.find(lt => lt.id === letterType)?.name || letterType;
    const htmlContent = marked(generatedLetter, { async: false }) as string;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${letterTypeName}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
              line-height: 1.6;
              color: #1f2937;
              max-width: 800px;
              margin: 0 auto;
              padding: 40px 20px;
            }
            h1 { font-size: 24px; font-weight: bold; margin-bottom: 8px; margin-top: 24px; }
            h2 { font-size: 20px; font-weight: bold; margin-top: 20px; margin-bottom: 12px; }
            h3 { font-size: 18px; font-weight: 600; margin-top: 16px; margin-bottom: 8px; }
            h4 { font-size: 16px; font-weight: 600; margin-top: 12px; margin-bottom: 8px; }
            p { margin-bottom: 12px; line-height: 1.6; }
            ul, ol { margin-left: 24px; margin-bottom: 12px; }
            li { margin-bottom: 4px; line-height: 1.6; }
            strong { font-weight: 600; color: #111827; }
            em { font-style: italic; }
            code { background-color: #f3f4f6; padding: 2px 6px; border-radius: 3px; font-family: 'Courier New', monospace; }
            pre { background-color: #1f2937; color: #f3f4f6; padding: 12px; border-radius: 6px; overflow-x: auto; }
            blockquote { border-left: 4px solid #60a5fa; padding-left: 16px; font-style: italic; margin: 12px 0; color: #374151; }
            @media print {
              body { padding: 20px; }
            }
          </style>
        </head>
        <body>
          <div class="content">
            ${htmlContent}
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const exportDocument = async (format: 'word' | 'text' | 'pdf') => {
    if (!generatedLetter) return;

    try {
      const letterTypeName = availableLetterTypes.find(lt => lt.id === letterType)?.name || letterType;
      const extensionMap: Record<string, string> = { word: 'docx', pdf: 'pdf', text: 'txt' };
      const extension = extensionMap[format];
      const filename = `${letterTypeName}.${extension}`;

      const blob = await exportAPI.exportDocument({
        content: generatedLetter,
        title: letterTypeName,
        format: extension as 'docx' | 'pdf' | 'txt'
      });

      // Try to use File System Access API
      if ('showSaveFilePicker' in window) {
        try {
          const mimeTypes: Record<string, string> = {
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            pdf: 'application/pdf',
            txt: 'text/plain'
          };

          const descriptions: Record<string, string> = {
            word: 'Word Document',
            pdf: 'PDF Document',
            text: 'Text File'
          };

          const handle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: descriptions[format],
              accept: { [mimeTypes[extension]]: [`.${extension}`] }
            }]
          });

          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          return;
        } catch (err: any) {
          if (err.name !== 'AbortError') {
            console.warn('File System Access API failed, falling back to standard download:', err);
          } else {
            return;
          }
        }
      }

      // Fallback: Standard download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export document. Please try again.');
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = () => {
    setGeneratedLetter('');
    setShowGeneratedLetter(false);
    setShowDeleteDialog(false);

    if (currentActivityId) {
      const { deleteActivity } = useActivitiesStore.getState();
      deleteActivity(currentActivityId);
      setCurrentActivityId(null);
    }
  };

  return (
    <div className="flex flex-col items-center h-full p-8">
      {/* Header */}
      <div className="mb-8">
        <img
          src="/scales-logo.png"
          alt="Scales of Justice"
          className="h-24 w-24 mx-auto mb-4"
        />
        <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100">{t('legalLetters:title')}</h1>
      </div>

      {/* Form */}
      <div className="w-full max-w-3xl space-y-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 w-56">
              {t('legalLetters:country')} <span className="text-red-500">*</span>:
            </label>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              required
              disabled={showLetterFields}
              className="flex-1 px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
            >
              <option value="">{t('legalLetters:selectCountry')}</option>
              {availableCountries.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          {availableStates.length > 0 && (
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 w-56">
                {t('legalLetters:state')} <span className="text-red-500">*</span>:
              </label>
              <select
                value={state}
                onChange={(e) => setState(e.target.value)}
                required
                disabled={showLetterFields}
                className="flex-1 px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
              >
                <option value="">{t('legalLetters:selectState')}</option>
                {availableStates.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          )}

          {availableLetterTypes.length > 0 && (
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 w-56">
                {t('legalLetters:letterType')} <span className="text-red-500">*</span>:
              </label>
              <select
                value={letterType}
                onChange={(e) => setLetterType(e.target.value)}
                required
                disabled={showLetterFields}
                className="flex-1 px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
              >
                <option value="">{t('legalLetters:selectLetterType')}</option>
                {availableLetterTypes.map((lt) => (
                  <option key={lt.id} value={lt.id}>
                    {t(`legalLetters:letterTypes.${lt.name}`, { defaultValue: lt.name })}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Message when no templates available for selected country/state */}
          {noTemplatesAvailable && country && state && (
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-amber-800 dark:text-amber-200 text-sm">
                {t('legalLetters:noTemplatesAvailable', {
                  country,
                  state,
                  defaultValue: `No legal letter templates are currently available for ${state}, ${country}. Templates for this jurisdiction will be added in a future update.`
                })}
              </p>
            </div>
          )}

          {!showLetterFields && (
            <div className="pt-4">
              <button
                type="submit"
                disabled={loading || noTemplatesAvailable}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {loading ? t('legalLetters:loading') : t('legalLetters:selectLetterType')}
              </button>
            </div>
          )}
        </form>

        {/* Dynamic Letter Fields */}
        {showLetterFields && letterFields.length > 0 && (
          <div className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">{t('legalLetters:letterInformation')}</h2>
            {letterFields.map((field, index) => (
              <div key={index} className="flex items-start gap-4">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 w-56 pt-2">
                  {field.label}:
                </label>
                {field.isMultiline ? (
                  <textarea
                    value={field.value}
                    onChange={(e) => handleFieldChange(index, e.target.value)}
                    placeholder={field.placeholder || t('legalLetters:enterValue', { field: field.label.toLowerCase() })}
                    rows={3}
                    className="flex-1 px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400 dark:placeholder-gray-500 resize-y"
                  />
                ) : (
                  <input
                    type="text"
                    value={field.value}
                    onChange={(e) => handleFieldChange(index, e.target.value)}
                    placeholder={field.placeholder || t('legalLetters:enterValue', { field: field.label.toLowerCase() })}
                    className="flex-1 px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400 dark:placeholder-gray-500"
                  />
                )}
              </div>
            ))}

            {/* Generate Button */}
            <div className="pt-4">
              <button
                onClick={handleGenerate}
                disabled={loading}
                className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:ring-4 focus:ring-green-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {loading ? t('legalLetters:processing') : t('legalLetters:generateLetter')}
              </button>
            </div>
          </div>
        )}

        {/* Generated Letter Display */}
        {showGeneratedLetter && generatedLetter && (
          <div className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('legalLetters:generatedLetter')}</h2>
              <div className="flex items-center gap-3">
                <ExportDropdownMenu onExport={handleExport} onPrint={printLetter} showPrint={true} />
                <button
                  onClick={handleDeleteClick}
                  className="text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  title="Delete this letter"
                >
                  <TrashIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="p-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <pre className="whitespace-pre-wrap font-sans text-gray-900 dark:text-gray-100 leading-relaxed">
                {generatedLetter}
              </pre>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {showDeleteDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                {t('legalLetters:deleteActivity')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {t('legalLetters:confirmDelete')}
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={handleDeleteConfirm}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                >
                  {t('legalLetters:delete')}
                </button>
                <button
                  onClick={() => setShowDeleteDialog(false)}
                  className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  {t('legalLetters:cancel')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
